// Editor state for user-defined symbols + go-to-definition (F12 / Ctrl-click).
import { StateField, StateEffect, EditorSelection } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { EMPTY_SYMBOLS, type SymbolTable, type SymbolDef } from './symbols';

export const setSymbolsEffect = StateEffect.define<SymbolTable>();

export const symbolsField = StateField.define<SymbolTable>({
  create: () => EMPTY_SYMBOLS,
  update(value, tr) {
    for (const e of tr.effects) if (e.is(setSymbolsEffect)) return e.value;
    return value;
  },
});

export function symbolsOf(state: EditorView['state']): SymbolTable {
  return state.field(symbolsField, false) ?? EMPTY_SYMBOLS;
}

/** Identifier at `pos` (or ending at it), or null. */
export function identifierAt(doc: string, pos: number): { word: string; from: number; to: number } | null {
  const isId = (c: string | undefined) => !!c && /[a-zA-Z0-9_$]/.test(c);
  let from = pos, to = pos;
  while (from > 0 && isId(doc[from - 1])) from--;
  while (to < doc.length && isId(doc[to])) to++;
  if (from === to) return null;
  const word = doc.slice(from, to);
  return /^[a-zA-Z_$]/.test(word) ? { word, from, to } : null;
}

/** Definition for the identifier at `pos`: a call `name(` prefers modules/functions. */
export function definitionAt(state: EditorView['state'], pos: number): SymbolDef | null {
  const doc = state.doc.toString();
  const id = identifierAt(doc, pos);
  if (!id) return null;
  const t = symbolsOf(state);
  const isCall = /^\s*\(/.test(doc.slice(id.to, id.to + 40));
  return isCall
    ? t.modules.get(id.word) ?? t.functions.get(id.word) ?? t.variables.get(id.word) ?? null
    : t.variables.get(id.word) ?? t.modules.get(id.word) ?? t.functions.get(id.word) ?? null;
}

function jumpTo(view: EditorView, def: SymbolDef): void {
  view.dispatch({
    selection: EditorSelection.range(def.nameFrom, def.nameTo),
    effects: EditorView.scrollIntoView(def.nameFrom, { y: 'center' }),
  });
  view.focus();
}

export function goToDefinition(view: EditorView): boolean {
  const def = definitionAt(view.state, view.state.selection.main.head);
  if (!def) return false;
  jumpTo(view, def);
  return true;
}

/** F12 and Ctrl/Cmd-click jump to the definition of a user symbol. */
export const goToDefinitionExtension = [
  keymap.of([{ key: 'F12', run: goToDefinition }]),
  EditorView.domEventHandlers({
    mousedown(event, view) {
      if (!(event.ctrlKey || event.metaKey) || event.button !== 0) return false;
      const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
      if (pos === null) return false;
      const def = definitionAt(view.state, pos);
      if (!def) return false;
      event.preventDefault();
      jumpTo(view, def);
      return true;
    },
  }),
];
